import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  Input,
  OnInit,
  output,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Router, RouterLink } from '@angular/router';
import { DatePipe, JsonPipe, SlicePipe } from '@angular/common';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { DateRange, MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_MENU_DEFAULT_OPTIONS, MatMenu, MatMenuModule, MenuCloseReason } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { IconComponent } from '@teamfoster/sdk/icon';
import { FromDictionaryPipe } from '@teamfoster/sdk/dictionary-ngrx';
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';
import { MatRadioModule } from '@angular/material/radio';
import { EventDatePipe } from '../../pipes/event-date.pipe';

interface FilterOption {
  key: string;
  dict?: string;
  options: { name: string; id: number | string; hint?: string; dateRange?: { from?: Date; to?: Date } }[];
}
@Component({
  selector: 'app-event-filters',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    JsonPipe,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatMenuModule,
    MatCheckboxModule,
    IconComponent,
    FromDictionaryPipe,
    SlicePipe,
    MatRadioModule,
    RouterLink,
  ],
  templateUrl: './event-filters.component.html',
  styleUrl: './event-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_MENU_DEFAULT_OPTIONS, useValue: { overlayPanelClass: 'panel-cloud-menu' } },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        overlayPanelClass: 'panel-cloud',
      },
    },
  ],
})
export class EventFiltersComponent implements OnInit, AfterViewInit {
  constructor(private dateAdapter: DateAdapter<Date>) {}

  // Daterange
  from = input<string>();
  to = input<string>();

  filterOptions = input<FilterOption[]>([]);
  filterValues = input<{ dateRange?: DateRange<Date> | Date | null; [key: string]: string[] | any }>({});

  clicked = signal(false);

  showByDefault = 3;

  filterValuesUpdated = effect(() => {
    const update = this.filterValues();
    if (!Object.keys(update).length) {
      this.filterForm.reset();
      this.updateCalendarFromFormvalue();
      this.cd.detectChanges();
    }
  });

  displayFilterValueKeys = computed(() => {
    const keys = Object.keys(this.filterValues() || {});

    //remove 'to' from keys
    const index = keys.indexOf('to');
    if (index > -1) {
      keys.splice(index, 1);
    }

    // remove 'from' from keys if 'date' is present
    if (keys.includes('date')) {
      const fromIndex = keys.indexOf('from');
      if (fromIndex > -1) {
        keys.splice(fromIndex, 1);
      }
    }

    return keys.filter(a => this.filterValues()[a].length > 0);
  });

  activeFilterCount = computed(() => {
    // total of all arrays in filterValues
    return Object.keys(this.filterValues() || {})
      .filter(a => a !== 'date' && a !== 'to')
      .reduce((acc, key) => {
        return (acc || 0) + (this.filterValues()[key].length || 0);
      }, 0);
  });

  public hasFiltersActive$ = computed(() => {
    return this.displayFilterValueKeys()?.length > 0;
  });

  router = inject(Router);
  cd = inject(ChangeDetectorRef);
  fb = inject(FormBuilder);

  filterForm: FormGroup = this.fb.group({});
  //filtersChanged = output<{ [key: string]: string[] }>();

  calendar = viewChild<MatCalendar<Date>>('calendar');
  dateFormatSting = 'yyyy-MM-dd'; // format for queryparam

  showAllFilters = signal(false);
  minDate = new Date(0); // same as archive from date

  dateRangeValue: Signal<DateRange<Date> | Date | null> = computed(() => {
    const from = this.from();
    const to = this.to();

    if (from && to) {
      return new DateRange(parse(from, this.dateFormatSting, new Date()), parse(to, this.dateFormatSting, new Date()));
    }

    return from ? parse(from, this.dateFormatSting, new Date()) : null;
  });

  dateValueCount = computed(() => {
    //const val: string[] = this.filterValues()?.['date'];
    //const from = this.from() ? 1 : 0;

    //return (val?.length || 0) + from;
    return this.from() || this.to() || this.filterValues()?.['date'] ? 1 : 0;
  });

  ngOnInit(): void {
    this.filterForm = this.fb.group(
      this.filterOptions()
        ? this.filterOptions()!.reduce((acc: { [key: string]: any }, option) => {
            acc[option.key] = [this.filterValues()?.[option.key]];
            return acc;
          }, {})
        : {}
    );

    this.filterForm.addControl('dateRange', this.fb.control<DateRange<Date> | Date | null>(this.dateRangeValue()));

    // Set to true if a filter option has a value thats hidden otherwise
    this.showAllFilters.set(
      this.filterOptions()
        .map((option, i) => ({ i, hasItems: !!this.filterValues()[option.key] }))
        .some(a => a.hasItems && a.i > this.showByDefault - 1)
    );

    // set first day of week to monday (1)
    this.dateAdapter.getFirstDayOfWeek = () => {
      return 1;
    };
  }

  ngAfterViewInit(): void {
    this.patchFilterValues();
  }

  patchFilterValues() {
    // patch dropdown values
    if (this.filterValues()) {
      this.filterForm.patchValue(this.filterValues()!);

      // patch date as single value (is radio button)
      const dateValue = this.filterValues()?.['date'];
      this.filterForm.get('date')?.setValue(Array.isArray(dateValue) ? dateValue[0] : dateValue);

      //this.filtersChanged.emit(this.filterForm.value);
    }

    // patch calendar
    this.updateCalendarFromFormvalue();

    this.cd.detectChanges();
  }

  updateCalendarFromFormvalue() {
    if (this.calendar()) {
      this.calendar()!.selected = this.filterForm.get('dateRange')?.value;
      this.calendar()!.updateTodaysDate();
    }
  }

  filterChange(key: string, value: any) {
    this.cd.detectChanges();
    this.router.navigate([], {
      queryParams: {
        [key]: value,
      },
      queryParamsHandling: 'merge',
    });
  }

  getValue(key: string, value: string) {
    if (key === 'from') {
      const date: Date = this.filterForm.get('dateRange')?.value;

      if (!date) {
        return null;
      }
      if (date instanceof DateRange) {
        return new EventDatePipe().transform(date.start, date.end, true);
      } else {
        return new EventDatePipe().transform(date.toString());
      }
    }

    return this.filterOptions()
      ?.filter(option => option.key === key)[0]
      ?.options.filter(option => `${option.id}` === `${value}`)[0]?.name;
  }

  removeFilter(key: string, value: string) {
    if (key === 'from') {
      this.filterForm.get('dateRange')?.setValue(null);
      this.updateCalendarFromFormvalue();
      this.cd.detectChanges();
      this.router.navigate([], {
        queryParams: {
          from: null,
          to: null,
        },
        queryParamsHandling: 'merge',
      });
      return;
    }

    const control = this.filterForm.get(key);

    if (key === 'date') {
      control?.setValue(null);
      this.filterForm.get('dateRange')?.setValue(null);
      this.cd.detectChanges();
      this.router.navigate([], {
        queryParams: {
          date: null,
          from: null,
          to: null,
        },
        queryParamsHandling: 'merge',
      });
      return;
    }

    control?.setValue(control.value.filter((val: string) => `${val}` !== `${value}`));
    this.filterChange(key, control?.value);
  }

  handleCalendar(filterKey: string, d: Date | null, event?: MouseEvent) {
    const target = event?.target as HTMLElement;
    const targetClass = (<Element>target?.parentNode)?.className || '';

    if (
      targetClass.includes('mat-calendar-next-button') ||
      targetClass.includes('mat-calendar-previous-button') ||
      targetClass.includes('mat-calendar-controls')
    ) {
      return;
    }

    const control = this.filterForm.get('dateRange');

    let inputDate: Date | null = null;

    if (!d && !(control?.value instanceof DateRange) && this.clicked()) {
      inputDate = control?.value as Date;
    }

    if (!d && !inputDate) {
      return;
    }

    let date = d ? new Date(d) : inputDate!;

    // reset preset option
    this.filterForm.get('date')?.setValue(null);

    // Set as startDate if no date is selected
    // or if the range is already set and the new date is before the start date
    // or if the clicked date is before the startdate
    // otherwise set as endDate
    if (!control?.value || (control.value as any) instanceof DateRange || date < control.value) {
      control?.setValue(date);
    } else {
      control.setValue(new DateRange(<Date>control.value, date));
    }

    // update calendar view
    if (this.calendar()) {
      this.calendar()!.selected = control?.value;
    }

    // Update queryparams
    const val = control?.value;
    const start = val instanceof DateRange ? val.start : val;
    const end = val instanceof DateRange ? val.end : null;

    this.clicked.set(false);

    if (end) {
      this.router.navigate([], {
        queryParams: {
          date: null,
          from: start ? format(start, this.dateFormatSting) : null,
          to: end ? format(end, this.dateFormatSting) : format(start, this.dateFormatSting),
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  handleDefaultDateOption(filterKey: string) {
    const control = this.filterForm.get(filterKey);
    const filter = this.filterOptions().filter(a => a.key === filterKey)?.[0];
    if (!control) {
      return;
    }

    const option = filter?.options.filter(a => a.id === control?.value)[0];
    const from = option.dateRange?.from || null;
    const to = option.dateRange?.to || null;

    const isDefaultOptionToday = control.value === 'today';

    this.router.navigate([], {
      queryParams: {
        date: control.value,
        from: from ? format(from, this.dateFormatSting + (isDefaultOptionToday ? '-HH.mm' : '')) : null,
        to: to ? format(to, this.dateFormatSting) : null,
      },
      queryParamsHandling: 'merge',
    });

    const dateRange = new DateRange(from, to);
    this.filterForm.get('dateRange')?.setValue(dateRange);

    // update calendar view
    if (this.calendar()) {
      this.calendar()!.selected = dateRange;
      this.calendar()?.updateTodaysDate();
    }

    this.cd.detectChanges();
  }

  getChecked(key: string, value: number | string) {
    return this.filterForm.get(key)?.value?.includes(value);
  }

  getOptionDict(key: string) {
    return this.filterOptions().filter(a => a.key === key)?.[0]?.dict;
  }
}
